import {gsap} from "gsap";
import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../../core/winpresentation/SimplePresentationRunner";
import {WinConfig, WinConfigUtil} from "../../../utils/WinConfigUtil";
import {SymbolUtils} from "../../../utils/SymbolUtils";
import {StateMatrixCoordinate} from "../../../utils/StateMatrix";
import {BS2} from "../../../BS2";
import {BS2Symbol, BS2SymbolStates} from "../../../reels/BS2Symbol";
import {BS2Statics} from "../../../config/BS2Statics";
import {BetWayWin, WinLineData} from "../../../server/BS2Data";
import {BS2Sound} from "../../../config/sound/BS2Sound";
import {SimpleReelModelSymbolData} from "../../../../core/reel/simplereel/SimpleReelModel";


/**
 * Created by jonas on 2022-01-12.
 */

export class DrainAndRemoveWinSymbols implements SimplePresentation {

    name: string = "DrainSymbols";

    constructor() {

    }

    start(controller: SimplePresentationRunnerController): void {
        const tl: gsap.core.Timeline = gsap.timeline({onComplete: () => controller.finish()});

        tl.add(this.getSymbolDrainAndRemoveAnimations());

        if (!BS2.model.isSuperFreespin()) {
            const addToBar: gsap.core.Timeline | undefined = BS2.reelView.collectBar.addToBarWithTotalCollected(BS2.model.data.totalCollectedSymbols);
            if (addToBar) {
                tl.add(addToBar, "<1.2");
            }
        }

        tl.addLabel("ending");
        tl.add(() => {
            controller.setSkipCallback();
        });

    }

    shouldRun(): boolean {
        return !BS2.model.gameData.brokeTheBank && BS2.model.gameData.betWayWins.length > 0;
    }

    private getAllConfigsForLevel(levelName: string, winConfigs: WinConfig[]) {
        const result = [];
        for (let conf of winConfigs) {
            if (conf.name == levelName) {
                result.push(conf);
            }
        }
        return result;
    }

    private getSymbolDrainAndRemoveAnimations(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            winSymbols: SimpleReelModelSymbolData[][] = SymbolUtils.getAllSymbolsWithStateByReel(BS2SymbolStates.WIN_HIGHLIGHT),
            noWinSymbols: SimpleReelModelSymbolData[] = SymbolUtils.getAllSymbolsWithState(BS2SymbolStates.NO_WIN);

        let winState: BS2SymbolStates = BS2SymbolStates.WIN_REMOVE;
        const winSymbolsTl: gsap.core.Timeline = gsap.timeline();

        let drain: boolean = false;
        const symbolDelay: number = 0; //BS2Statics.WIN_SYMBOL_DELAY;

        for (let reel of winSymbols) {
            const result: gsap.core.Timeline[] = [];
            //Reverse to add the symbols bottom to top.
            for (let sym of reel.reverse()) {
                const doDrain: boolean = BS2Statics.MID_SYMBOLS.indexOf(sym.name) >= 0;
                sym.removed = true;
                if (!drain) {
                    drain = doDrain;
                }

                winState = doDrain ? BS2SymbolStates.WIN_DRAIN : BS2SymbolStates.WIN_REMOVE

                const tl: gsap.core.Timeline = (<BS2Symbol>sym.positionable).getStateAnimation(winState);
                result.push(tl);
            }
            winSymbolsTl.add(result, `<${symbolDelay}`);
        }

        tl.add(() => {
            const isBigWin = BS2.model.winData.winRatio >= 18;
            const belowStakeWinRestriction = BS2.isWinBelowStake();
            //-----------DATA STUFF

            const multiplier = 1;
            let winConfigs: WinConfig[] = [];

            if (!isBigWin) {
                for (let i: number = 1; i <= multiplier; i++) {
                    const startRatio = BS2.model.winData.winRatio * (i - 1);
                    const endRatio = BS2.model.winData.winRatio * i;
                    winConfigs = winConfigs.concat(WinConfigUtil.getNormalWinLevels(startRatio, endRatio, i, i == multiplier));

                }

                const lastLevelConfig: WinConfig = winConfigs[winConfigs.length - 1].clone();
                if ((belowStakeWinRestriction && !(BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2 || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1) && !BS2.isWinBelowStakeSpinWinnings()) || !belowStakeWinRestriction) {
                    const winRatioIndexes: string [] = ["TINY", "SMALL", "MEDIUM", "LARGE"];
                    const indexToPlay: number = winRatioIndexes.indexOf(lastLevelConfig.name);
                    BS2.soundPlayer.playSound(BS2Sound.WIN_REMOVE_BY_RATIO[indexToPlay]);
                }
            } else {
                if ((belowStakeWinRestriction && !(BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2 || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1) && !BS2.isWinBelowStakeSpinWinnings()) || !belowStakeWinRestriction) {
                    BS2.soundPlayer.playSound(BS2Sound.WIN_REMOVE_BY_RATIO[3]);
                }
            }
        });
        // if (drain) {
        //     tl.add(() => BS2.soundPlayer.playSound(BS2Sound.SYMBOL_DRAIN));
        // }

        tl.add(winSymbolsTl);

        const winLineAmountData: { betWin: BetWayWin, reelPos: StateMatrixCoordinate }[] = this.getWinWayAmountData();
        if (winLineAmountData.length > 0) {
            tl.add((): void => {
                BS2.keypadPlugin.setWin(BS2.model.gameData.accumulatedRoundWin, true, BS2.isWinBelowStake())
            }, ">-0.6");
        }

        if (winSymbols[0].length > 0) {
            tl.add(SymbolUtils.getSymbolStateAnimations(noWinSymbols, BS2SymbolStates.NORMAL));
        }


        return tl;
    }

    private getWinWayAmountData(): { betWin: BetWayWin, reelPos: StateMatrixCoordinate }[] {
        const winWayAmountData: { betWin: BetWayWin, reelPos: StateMatrixCoordinate }[] = [];
        for (let i: number = 0; i < BS2.model.winData.lines.length; i++) {
            const line: WinLineData = BS2.model.winData.lines[i];
            const winPositions: StateMatrixCoordinate[] = line.matrix.getAllCoordinatesForValue(true);
            const lastPos: StateMatrixCoordinate = winPositions.pop()!;

            winWayAmountData.push({
                betWin: line.betWayWin,
                reelPos: lastPos,
            });
        }
        return winWayAmountData;
    }
}